import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  CreateConnectorRequestSchema,
  CreateConnectorResponseSchema,
  GetConnectorResponseSchema,
  ListConnectorsResponseSchema,
  GenerateTokensResponseSchema,
  CreateTwingateResourceRequestSchema,
  CreateTwingateResourceResponseSchema,
  GetTwingateResourceResponseSchema,
  ListTwingateResourcesResponseSchema,
  ListTwingateNetworksResponseSchema,
  CreateTwingateNetworkRequestSchema,
  CreateTwingateNetworkResponseSchema,
  GetTwingateNetworkResponseSchema,
  UpdateTwingateNetworkRequestSchema,
  UpdateTwingateNetworkResponseSchema,
  TwingateNetworkIdPathParamsSchema,
  RedeployTwingateNetworkProxyResponseSchema,
  CleanDeployTwingateNetworkProxyResponseSchema,
  ListConnectorsQuerySchema,
  ListTwingateResourcesQuerySchema,
} from './connector-manager.schema';
import {
  CreateTailscaleNetworkRequestSchema,
  CreateTailscaleNetworkResponseSchema,
  GetTailscaleNetworkResponseSchema,
  ListTailscaleNetworksResponseSchema,
  ProvisionTailscaleConnectorResponseSchema,
  TailscaleNetworkIdPathParamsSchema,
  UpdateTailscaleNetworkRequestSchema,
  UpdateTailscaleNetworkResponseSchema,
} from './tailscale-network.schema';
import { CONNECTOR_MANAGER_PATH_NAME } from './connector-manager.constants';
import { z } from 'zod';

export const ConnectorManagerContract = terraContract
  .prefix(`/${CONNECTOR_MANAGER_PATH_NAME}`)
  .router({
    createConnector: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'POST',
        path: '/connectors',
        summary: 'Create connector',
        description:
          'Creates a new connector for accessing internal/private environments. Connectors enable Terra to securely test applications that are not publicly accessible. After creation, deploy the connector agent in your environment using the provided tokens.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: CreateConnectorRequestSchema }))
      .output(CreateConnectorResponseSchema)
      .errors({
        BAD_REQUEST: {
          status: 400,
          data: z
            .object({
              status: z.number().describe('HTTP status code'),
              message: z.string().describe('Error message'),
              errors: z
                .array(
                  z.object({
                    validation: z.string().describe('Validation type'),
                    code: z.string().describe('Error code'),
                    message: z.string().describe('Error message'),
                    path: z
                      .array(z.string())
                      .describe('Path to the invalid field'),
                  }),
                )
                .describe('Validation errors'),
            })
            .describe('Validation error response'),
        },
      }),
    getConnector: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'GET',
        path: '/connectors/{connectorId}',
        summary: 'Get connector details',
        description:
          'Retrieves status and configuration details for a specific connector, including its health, last heartbeat, and connected resources.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ connectorId: z.string() }) }))
      .output(GetConnectorResponseSchema),
    listConnectors: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'GET',
        path: '/connectors',
        summary: 'List connectors',
        description:
          'Retrieves all connectors configured for your tenant. Each connector represents a secure tunnel to an internal/private environment.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: ListConnectorsQuerySchema }))
      .output(ListConnectorsResponseSchema),
    deleteConnector: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'DELETE',
        path: '/connectors/{connectorId}',
        summary: 'Delete connector',
        description:
          'Permanently deletes a connector and all associated resources. The connector agent will stop working immediately. This action cannot be undone.',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .input(z.object({ params: z.object({ connectorId: z.string() }) }))
      .output(z.null()),
    generateTokens: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'POST',
        path: '/connectors/{connectorId}/tokens',
        summary: 'Regenerate tokens',
        description:
          'Generates new authentication tokens for a connector. Use this if existing tokens are compromised or need rotation. The old tokens will be invalidated immediately.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ connectorId: z.string() }) }))
      .output(GenerateTokensResponseSchema),
    refreshConnector: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'GET',
        path: '/connectors/{connectorId}/refresh',
        summary: 'Refresh connector status',
        description:
          'Forces a refresh of the connector status by checking its current health. Use this to get the latest health and connectivity information.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ connectorId: z.string() }) }))
      .output(GetConnectorResponseSchema),
    createTwingateResource: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'POST',
        path: '/resources',
        summary: 'Create resource',
        description:
          'Creates a new resource that defines an internal application or network segment accessible via a connector. Resources specify the address, ports, and protocols that Terra can use for testing.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: CreateTwingateResourceRequestSchema }))
      .output(CreateTwingateResourceResponseSchema)
      .errors({
        BAD_REQUEST: {
          status: 400,
          data: z
            .object({
              status: z.number().describe('HTTP status code'),
              message: z.string().describe('Error message'),
              errors: z
                .array(
                  z.object({
                    validation: z.string().describe('Validation type'),
                    code: z.string().describe('Error code'),
                    message: z.string().describe('Error message'),
                    path: z
                      .array(z.string())
                      .describe('Path to the invalid field'),
                  }),
                )
                .describe('Validation errors'),
            })
            .describe('Validation error response'),
        },
      }),
    getTwingateResource: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'GET',
        path: '/resources/{resourceId}',
        summary: 'Get resource details',
        description:
          'Retrieves configuration and status for a specific resource.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ resourceId: z.string() }) }))
      .output(GetTwingateResourceResponseSchema),
    listTwingateResources: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'GET',
        path: '/resources',
        summary: 'List resources',
        description:
          'Retrieves all resources configured for your tenant. Resources are the internal endpoints that Terra can access through your connectors.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: ListTwingateResourcesQuerySchema }))
      .output(ListTwingateResourcesResponseSchema),
    deleteTwingateResource: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'DELETE',
        path: '/resources/{resourceId}',
        summary: 'Delete resource',
        description:
          'Removes a resource. Terra will no longer be able to access this internal endpoint for testing.',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .input(z.object({ params: z.object({ resourceId: z.string() }) }))
      .output(z.null()),
    listTwingateNetworks: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'GET',
        path: '/networks',
        summary: 'List networks',
        description:
          'Lists all connector networks configured for the tenant, including the default network.',
        inputStructure: 'detailed',
      })
      .output(ListTwingateNetworksResponseSchema),
    createTwingateNetwork: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'POST',
        path: '/networks',
        summary: 'Create network',
        description:
          'Creates an additional connector network with its own proxy, group, and service account.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: CreateTwingateNetworkRequestSchema }))
      .output(CreateTwingateNetworkResponseSchema),
    getTwingateNetwork: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'GET',
        path: '/networks/{networkId}',
        summary: 'Get network',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: TwingateNetworkIdPathParamsSchema }))
      .output(GetTwingateNetworkResponseSchema),
    updateTwingateNetwork: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'PATCH',
        path: '/networks/{networkId}',
        summary: 'Update network',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: TwingateNetworkIdPathParamsSchema,
          body: UpdateTwingateNetworkRequestSchema,
        }),
      )
      .output(UpdateTwingateNetworkResponseSchema),
    deleteTwingateNetwork: terraContract
      .meta({
        permissions: [PERMISSIONS.CREATE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'DELETE',
        path: '/networks/{networkId}',
        summary: 'Delete network',
        description:
          'DANGEROUS: Permanently deletes the network and all connectors and resources on it. Customers must reinstall connectors afterward. Also removes the network proxy and clears application network bindings. Does not only remove the proxy — use clean-deploy-proxy for that. The default network cannot be deleted while other networks exist.',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .input(z.object({ params: TwingateNetworkIdPathParamsSchema }))
      .output(z.null()),
    redeployTwingateNetworkProxy: terraContract
      .meta({
        permissions: [PERMISSIONS.CREATE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'POST',
        path: '/networks/{networkId}/redeploy-proxy',
        summary: 'Redeploy network proxy',
        description:
          'Forces a reconcile of the network proxy deployment for the given network.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: TwingateNetworkIdPathParamsSchema }))
      .output(RedeployTwingateNetworkProxyResponseSchema),
    cleanDeployTwingateNetworkProxy: terraContract
      .meta({
        permissions: [PERMISSIONS.CREATE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'POST',
        path: '/networks/{networkId}/clean-deploy-proxy',
        summary: 'Clean deploy network proxy',
        description:
          'Deletes existing proxy resources for this network (including legacy-named default proxy objects when applicable), then recreates them. Does not delete the network, connectors, or resources.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: TwingateNetworkIdPathParamsSchema }))
      .output(CleanDeployTwingateNetworkProxyResponseSchema),
    listInternalAccessNetworks: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'GET',
        path: '/internal-access-networks',
        summary: 'List internal access networks',
        inputStructure: 'detailed',
      })
      .output(ListTailscaleNetworksResponseSchema),
    createInternalAccessNetwork: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'POST',
        path: '/internal-access-networks',
        summary: 'Create internal access network',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: CreateTailscaleNetworkRequestSchema }))
      .output(CreateTailscaleNetworkResponseSchema),
    getInternalAccessNetwork: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'GET',
        path: '/internal-access-networks/{networkId}',
        summary: 'Get internal access network',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: TailscaleNetworkIdPathParamsSchema }))
      .output(GetTailscaleNetworkResponseSchema),
    updateInternalAccessNetwork: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'PATCH',
        path: '/internal-access-networks/{networkId}',
        summary: 'Update internal access network',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: TailscaleNetworkIdPathParamsSchema,
          body: UpdateTailscaleNetworkRequestSchema,
        }),
      )
      .output(UpdateTailscaleNetworkResponseSchema),
    deleteInternalAccessNetwork: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'DELETE',
        path: '/internal-access-networks/{networkId}',
        summary: 'Delete internal access network',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .input(z.object({ params: TailscaleNetworkIdPathParamsSchema }))
      .output(z.null()),
    provisionInternalAccessConnector: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
        openApi: true,
      })
      .route({
        method: 'POST',
        path: '/internal-access-networks/{networkId}/connector',
        summary: 'Provision internal access connector install bundle',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: TailscaleNetworkIdPathParamsSchema }))
      .output(ProvisionTailscaleConnectorResponseSchema),
  });
